/**
 * 個人設定の端末キャッシュと、旧 localStorage キーの吸い上げ（PRODUCT_DEV-699）
 * ============================================================================
 *
 * 設定の**正はサーバ**（`UnizoPreferences` / `GET,PATCH /user-preferences`）。
 * localStorage は「サーバ応答が返るまでの下書き」として残す。
 *
 * ## なぜ下書きを残すのか
 *
 * 記録ドックはプリセットを `useState` の初期化関数で**同期的に**決めている。
 * サーバ応答（数百ミリ秒）を待つ形にすると、開くたびに部署デフォルトが一瞬出てから
 * 選択済みのものに切り替わる。下書きがあれば従来どおり一発で正しい値が出る。
 *
 * ⚠ **消えた直後の1回だけは救えない。** キャッシュが無い人は初回だけ既定が一瞬出る。
 * 2回目以降はサーバ由来の値がキャッシュに載るので解消する。
 *
 * ## キャッシュはテナントスコープとして扱う
 *
 * サーバ側の PK は `PRODUCT#..#TENANT#..#USER#..` でテナントごとに分かれる。
 * キャッシュは分かれないので、**テナント切替では9キー全部を捨てる**
 * （`utils/tenantSwitch.ts`）。捨てないと切替直後に前テナントの値が一瞬出るし、
 * presetId は他テナントで解決できず PRODUCT_DEV-610 と同じ壊れ方をする。
 */
import {
  PatchUserPreferencesSchema,
  USER_PREFERENCE_KEYS,
  type PatchUserPreferences,
  type UserPreferences,
} from "@shared-domain/unizo/contract";
import {
  getLocalStorageItem,
  getRawLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "@shared-ui/utils/localStorage";

/** 設定の項目名（`UserPreferences` のフィールド名）。 */
export type PreferenceKey = keyof UserPreferences;

export const PREFERENCE_KEYS = Object.keys(
  USER_PREFERENCE_KEYS,
) as PreferenceKey[];

/**
 * キャッシュの localStorage キー。
 *
 * 旧キーと衝突しない前置きにしてある。旧キーは吸い上げ（seed）専用で、
 * 成功したら消す——残すと、**別テナントへ切り替えて設定が空のとき**に
 * 前テナントの旧キーを吸い上げてしまう。
 */
export const cacheKey = (key: PreferenceKey): string => `unizo-pref:${key}`;

export const readCache = (key: PreferenceKey): unknown =>
  getLocalStorageItem<unknown>(cacheKey(key), null);

/** 設定名 → 値。未設定のキーは入らない。 */
export type PreferenceValues = Partial<UserPreferences>;

/**
 * 初期描画に使う値。**旧キー → キャッシュ**の順に重ね、キャッシュを優先する。
 *
 * 旧キーを混ぜるのは**リリース直後の1回**のために要る。そのときキャッシュはまだ空で、
 * 本当の設定は旧キーにしかない。ここで読まないと、移行した最初のアクセスだけ
 * 全員の設定が初期値に戻って見える（まさに直したかった症状が出る）。
 *
 * どちらの経路の値も、端末上のデータなので手で書き換えられる。`pickValid` に通して
 * 壊れた項目だけを落とす。
 */
export const readInitialValues = (): PreferenceValues => {
  const merged: Record<string, unknown> = { ...readLegacyPreferences() };
  for (const key of PREFERENCE_KEYS) {
    const cached = readCache(key);
    if (cached !== null) merged[key] = cached;
  }
  return pickValid(merged) as PreferenceValues;
};

export const writeCache = (key: PreferenceKey, value: unknown): void => {
  if (value === null || value === undefined) removeLocalStorageItem(cacheKey(key));
  else setLocalStorageItem(cacheKey(key), value);
};

/** キャッシュを全部捨てる（テナント切替時）。 */
export const clearCache = (): void => {
  for (const key of PREFERENCE_KEYS) removeLocalStorageItem(cacheKey(key));
};

/*
 * ---------------------------------------------------------------------------
 * 旧キーからの吸い上げ（seed）
 * ---------------------------------------------------------------------------
 * リリース時点で端末に残っている値をサーバへ1回だけ上げる。これが無いと
 * 「一度も触らない設定」は永久にサーバへ上がらず、端末を変えた瞬間に失われる。
 */

/** 吸い上げ元。読み終わったら消すので、ここが唯一の一覧になる。 */
export const LEGACY_KEYS = [
  "unizo-dock-last-preset-id",
  "speed-recording-last-preset-id",
  "unizo-dock-preset-chosen",
  "unizo-dock-last-mode",
  "umeee_record_mode",
  "unizo-dock-collapsed",
  "notificationBadgeViewedAt",
  "unizo-last-seen-release-version",
  "unizo.scheduling.calendarHintDismissed",
  "unizo-ai-voice-volume",
  "unizo-search-history",
] as const;

/** 生の文字列で保存されていたもの（`JSON.stringify` を通していない）。 */
const raw = (key: string): string => getRawLocalStorageItem(key, "");

/** `setLocalStorageItem` で保存されていたもの。 */
const json = <T>(key: string): T | null => getLocalStorageItem<T | null>(key, null);

/**
 * 旧キーを読んで PATCH に載せられる形にする。
 *
 * **記録モードとプリセットは2箇所に分かれていたので、ここで1つに寄せる。**
 * 優先は記録ドック側（ホームの主導線で、利用頻度が高いほう）。
 *
 * ⚠ ドックの `"dialog"` は**このリポジトリで唯一の綴り**で、商談レコードの
 * `recordingMode` と顧客ごとの `lastRecordingMode` は `"dialogue"`。
 * 設定側は多数派に合わせるので、ここで読み替える。
 */
export const readLegacyPreferences = (): PatchUserPreferences => {
  const candidate: Record<string, unknown> = {};

  const presetId = raw("unizo-dock-last-preset-id") || raw("speed-recording-last-preset-id");
  if (presetId) candidate.lastPresetId = presetId;

  if (raw("unizo-dock-preset-chosen") === "1") candidate.presetChosen = true;

  const dockMode = raw("unizo-dock-last-mode");
  const legacyMode = json<string>("umeee_record_mode");
  const mode = dockMode || legacyMode;
  if (mode) candidate.recordMode = mode === "dialog" ? "dialogue" : mode;

  const collapsed = raw("unizo-dock-collapsed");
  if (collapsed === "1" || collapsed === "0") candidate.dockCollapsed = collapsed === "1";

  const viewedAt = raw("notificationBadgeViewedAt");
  if (viewedAt) candidate.notificationViewedAt = viewedAt;

  const version = json<string>("unizo-last-seen-release-version");
  if (version) candidate.lastSeenReleaseVersion = version;

  if (raw("unizo.scheduling.calendarHintDismissed") === "1") {
    candidate.schedulingHintDismissed = true;
  }

  const volume = json<number>("unizo-ai-voice-volume");
  if (volume !== null) candidate.aiVoiceVolume = volume;

  const history = json<unknown[]>("unizo-search-history");
  if (history !== null) candidate.searchHistory = history;

  return pickValid(candidate);
};

/**
 * スキーマに通る項目だけを残す。
 *
 * **1項目ずつ検証して落とす**のが要点。まとめて `safeParse` して全部捨てると、
 * 壊れた1件（手で書き換えられた音量など）のせいで**他の設定まで吸い上げられない**。
 * 逆に検証せず送ると PATCH が 400 になって seed 全体が失敗する。
 */
const pickValid = (candidate: Record<string, unknown>): PatchUserPreferences => {
  const out: Record<string, unknown> = {};
  for (const [field, value] of Object.entries(candidate)) {
    const parsed = PatchUserPreferencesSchema.safeParse({ [field]: value });
    if (parsed.success) Object.assign(out, parsed.data);
  }
  return out as PatchUserPreferences;
};

/** 吸い上げ済みの旧キーを消す（seed 成功後・テナント切替時）。 */
export const clearLegacyPreferences = (): void => {
  for (const key of LEGACY_KEYS) removeLocalStorageItem(key);
};
